<template>
    <section v-if="json" class="page-archive">
        <!-- Top filter set -->
        <div class="capper">
            <div class="capper-padded">
                <div class="top-filter-set">
                    <!-- Year filter: Resets the letter filter and shows results by year -->
                    <div :class="['filter-item Zlink-underline-leftright-gray', (selectedLetter) ? '' : 'active']" @click="setSelectedLetter(null)">YEAR</div>

                    <!-- Alphabet letters -->
                    <div class="dot"></div>

                    <!-- Loop through A to Z for the alphabet filters -->
                    <div v-for="letter in alphabet" :key="letter" :class="['filter-item Zlink-underline-leftright-gray', (selectedLetter === letter) ? 'active' : '']" @click="setSelectedLetter(letter)">{{ letter }}</div>
                </div>
            </div>
        </div>

        <FilterSet/>
        <div class="capper">
            <div class="capper-padded">

                <!-- Letter view -->
                <div v-if="selectedLetter" class="archive-grid-by-letter" data-aos="custom-fade-in">
                    <h2>{{ selectedLetter }}</h2>
                    <div v-if="!byLetter?.length">
                        <div class="rte right">
                            <p v-if="!selectedFilters.length">No matches found with this letter.</p>
                            <p v-else>No matches found with this letter and filter.</p>
                        </div>
                    </div>
                    <div v-else class="grid-items">
                        <div v-for="(item, itemIndex) in byLetter" :key="`byLetterGridItem${itemIndex}`" class="grid-item" ref="gridItem">
                            <div class="grid-item-title">
                                <router-link v-if="item.url" :to="item.url" class="link-underline-leftright-dark">{{ getTruncatedTitle(item.title) }}</router-link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Year view -->
                <template v-if="!selectedLetter">
                    <div v-if="!byYear.length">
                        <div class="rte right">
                            <p>No matches found with this filter</p>
                        </div>
                    </div>
                    <div v-for="(yearObj) in byYear" :key="`year${yearObj.year}`" class="archive-grid-by-year" data-aos="custom-fade-in">
                        <h2>{{ yearObj.year }}</h2>
                        <div class="grid-items">
                            <div v-for="(item, itemIndex) in yearObj.items" :key="`byYearGridItem${itemIndex}`" class="grid-item" ref="gridItem">
                                <div class="grid-item-title">
                                    <router-link v-if="item.url" :to="item.url" class="link-underline-leftright-dark">{{ getTruncatedTitle(item.title) }}</router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </section>
    <section v-else class="page-archive">
        <Loading v-if="showLoading"/>
    </section>
</template>

<script>
import axios from 'axios'
import gh from '@/helpers/gh'
import { mapState } from "vuex";
import FilterSet from "@/components/blocks/FilterSet.vue";
import Loading from "@/components/blocks/Loading.vue";

export default {
    name: "ArchivePage",
    components: {
        Loading,
        FilterSet,
    },
    data() {
        return {
            window: window,
            json: null,
            results: [],
            filterToggler: Math.random(),
            availableWidth: 300,
            selectedLetter: null,
            alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(''),

            loadingTimer: null,
            showLoading: false
        };
    },
    mounted() {
        const urlParams = new URLSearchParams(this.$route.query);
        const archiveParam = urlParams.get("archive");

        // Set selectedLetter from the archive parameter if it exists
        if (archiveParam && this.alphabet.includes(archiveParam.toUpperCase())) {
            this.selectedLetter = archiveParam.toUpperCase();
        }

        // start a timer to only show Loading after 1s
        this.loadingTimer = setTimeout(() => {
            this.showLoading = true
        },1000)

        let slug = this.$route.fullPath;
        slug = gh.stripSlashesFrontAndBack(slug);
        axios.get(`${window.config.apiroot}${slug}/${window.config.craftApiPreviewToken}`)
            .then((res) => {
                this.json = res.data;
                this.results = this.json.projects;

                this.showLoading = false

                this.window.addEventListener('resize', this.updateAvailableWidth);
                this.updateAvailableWidth(); // Set width after data loads
            })
            .catch((e) => {
                console.error("Error", slug, e);
                this.showLoading = false
            });
    },
    beforeUnmount() {
        this.window.removeEventListener('resize', this.updateAvailableWidth);
    },
    computed: {
        ...mapState({
            searchTerm: state => state.searchTerm,
            selectedFilters: state => state.selectedFilters,
        }),
        filteredJson() {
            if (!this.filterToggler) {
                return this.results;
            }

            if (!this.selectedFilters || this.selectedFilters.length === 0) {
                return this.results;
            }

            return this.results.filter(item => {
                if (!item.projectCategory || !Array.isArray(item.projectCategory)) {
                    return false;
                }

                // MATCHES ANY OF THEM
                // return item.projectCategory.some(category =>
                //     this.selectedFilters.some(filter => filter.slug === category.slug)
                // );

                // MATCHES ALL OF THEM
                const catSlugs = item.projectCategory.map(c => c.slug);
                return this.selectedFilters.every(filter => catSlugs.includes(filter.slug));

            });
        },
        byYear() {
            if (!this.filteredJson || this.filteredJson.length === 0) {
                return [];
            }

            const groupedByYear = this.filteredJson.reduce((acc, item) => {
                const year = item.year;
                if (!acc[year]) {
                    acc[year] = [];
                }
                acc[year].push(item);
                return acc;
            }, {});

            return Object.keys(groupedByYear)
                .map(year => ({
                    year: parseInt(year, 10),
                    items: groupedByYear[year].sort((a, b) => a.title.localeCompare(b.title))
                }))
                .sort((a, b) => b.year - a.year);
        },
        byLetter() {
            if (!this.filteredJson || this.filteredJson.length === 0 || !this.selectedLetter) {
                return [];
            }

            return this.filteredJson
                .filter(item => item.title?.toUpperCase().startsWith(this.selectedLetter))
                .sort((a, b) => a.title.localeCompare(b.title));
        }
    },
    methods: {
        getTruncatedTitle(title) {
            const maxChars = Math.floor(this.availableWidth / 11); // assuming avg width of 10px per char

            if (title.length >= maxChars) {
                const startText = title.slice(0, maxChars - 8); // Leave room for ellipsis and 5 chars
                const endText = title.slice(-5); // Last 5 characters
                return `${startText}...${endText}`;
            }

            return title;
        },
        updateAvailableWidth() {
            this.$nextTick(() => {
                if (this.$refs.gridItem && this.$refs.gridItem.length > 0) {
                    const firstGridItem = this.$refs.gridItem[0];
                    if (firstGridItem) {
                        this.availableWidth = firstGridItem.clientWidth;
                    }
                }
            });
        },
        setSelectedLetter(letter) {
            this.selectedLetter = letter;

            // Manually parse the current query parameters from the URL
            const currentQuery = Object.fromEntries(new URLSearchParams(window.location.search));

            if (letter) {
                // Set or update the archive parameter
                currentQuery.archive = letter;
            } else {
                // Remove the archive parameter if no letter is selected
                delete currentQuery.archive;
            }

            // Rebuild the query string
            const queryString = new URLSearchParams(currentQuery).toString();
            const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

            // Update the URL manually without reloading the page
            window.history.replaceState({}, "", newUrl);
        }



    }
};
</script>
